<script lang="ts">
  // Wraps the family browser with a source switcher. The cloud catalog is one source; local folders
  // (Revit-only) are the others. In the browser there is only the cloud catalog, so the switcher is
  // hidden and this is just the catalog grid.

  import { onMount } from "svelte";
  import { transport } from "@/transport";
  import { sources } from "./sourcesStore.svelte";
  import CatalogView from "./CatalogView.svelte";
  import LocalCatalogView from "./LocalCatalogView.svelte";
  import { t } from "@/i18n/index.svelte";
  import { AppButton, AppSelect } from "@/components/ui";

  let addError = $state("");

  onMount(() => {
    void sources.load();
  });

  async function addFolder() {
    addError = "";
    const err = await sources.addFolder();
    if (err) addError = err;
  }

  function sourceLabel(kind: string, name: string): string {
    return kind === "cloud" ? t("sources.cloud") : name;
  }
</script>

<div class="flex flex-col gap-4">
  <!-- Source switcher — only meaningful in Revit, where local folders can exist.
       A labelled dropdown next to an add button, the way a package manager does it: the number of
       sources is open-ended (one catalog, then a folder per library somebody keeps), and a row of
       chips that grows without limit pushes the catalog itself further down the panel every time.
       A dropdown costs one line whatever the count, and it names the thing it is choosing. -->
  {#if transport.caps.revit}
    <div class="flex items-center gap-2">
      <label class="text-sm text-ink-muted shrink-0" for="source-select">{t("sources.source")}</label>
      <AppSelect
        id="source-select"
        size="sm"
        class="min-w-0 flex-1 max-w-xs"
        value={sources.state.selectedId}
        onchange={(e) => sources.select(e.currentTarget.value)}
      >
        {#each sources.state.sources as s (s.id)}
          <option value={s.id}>{sourceLabel(s.kind, s.name)}</option>
        {/each}
      </AppSelect>

      <AppButton variant="secondary" size="sm" title={t("sources.addFolder")} onclick={addFolder} class="shrink-0">
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span class="hidden sm:inline">{t("sources.addFolder")}</span>
      </AppButton>
    </div>
  {/if}
  {#if addError}
    <p class="text-xs text-error">{addError}</p>
  {/if}

  <!-- The selected source's grid. -->
  {#if sources.selected().kind === "cloud"}
    <CatalogView />
  {:else}
    <LocalCatalogView sourceId={sources.state.selectedId} />
  {/if}
</div>
